import {
  createStyles,
  PaletteOptions,
  ThemeOptions,
} from "@mui/material/styles";
import { colors } from "config/styles/themeColors";
import { fontSize, fonts } from "config/styles/fonts";

export const themeColors = () =>
  createStyles({
    palette: colors,
  }) as PaletteOptions;

export const theme = () =>
  createStyles({
    breakpoints: {
      values: {
        xs: 0,
        sm: 360,
        md: 768,
        lg: 1024,
        xl: 1280,
        xxl: 1920,
      },
    },
    typography: {
      h1: {
        fontFamily: fonts.text,
        fontWeight: 600,
        fontSize: fontSize.xxxl,
        lineHeight: "1.7",
        letterSpacing: 2,
        "@media (max-width: 1199px)": {
          fontSize: fontSize.xxl,
          letterSpacing: 0,
        },
      },
      h2: {
        fontFamily: fonts.text,
        fontWeight: 600,
        fontSize: fontSize.xxl,
        lineHeight: "1.8",
        letterSpacing: 1,
      },
      h3: {
        fontFamily: fonts.text,
      },
      h4: {
        fontFamily: fonts.text,
      },
      h5: {
        fontFamily: fonts.text,
      },
      h6: {
        fontFamily: fonts.text,
      },
      subtitle1: {
        fontFamily: fonts.text,
        fontSize: fontSize.l,
        fontWeight: 400,
        lineHeight: "1.2",
        color: colors.text.secondary,
        "@media (max-width: 1199px)": {
          fontSize: fontSize.s,
          letterSpacing: 0,
        },
      },
      caption: {
        fontFamily: fonts.text,
      },
      body1: {
        fontFamily: fonts.text,
        fontWeight: 400,
        fontSize: fontSize.m,
        letterSpacing: 0,
      },
      buttonText: {
        fontFamily: fonts.text,
        fontWeight: 500,
        fontSize: fontSize.m,
        lineHeight: "1",
        letterSpacing: 0,
      },
    },
    components: {
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            padding: "0",
            width: "100%",
            height: "40px",
            borderRadius: "6px",
            fontFamily: fonts.text,
            fontWeight: 500,
            fontSize: fontSize.m,
            lineHeight: "1",
            letterSpacing: 0,
            textTransform: "none",
            border: "2px solid",
            transition: "all 0.3s ease",
            "&.primaryBlack": {
              backgroundColor: colors.text.primary,
              borderColor: colors.text.primary,
              color: colors.background.default,
              "&:hover": {
                backgroundColor: colors.gray.main,
                borderColor: colors.gray.main,
              },
              "&:focus": {
                backgroundColor: colors.text.primary,
                borderColor: colors.text.primary,
                color: colors.background.default,
              },
              "&:active": {
                backgroundColor: colors.text.primary,
                borderColor: colors.text.primary,
                color: colors.background.default,
              },
              "&:disabled": {
                borderColor: colors.gray.disabled,
                backgroundColor: colors.gray.disabled,
                color: colors.background.default,
              },
            },
            "&.secondary": {
              backgroundColor: "inherit",
              borderColor: `${colors.gray.btnBorder} !important`,
              border: "1px solid",
              color: colors.text.primary,
              fontWeight: 700,
              letterSpacing: "1px",
              "&:hover": {
                backgroundColor: colors.background.button,
                borderColor: `${colors.gray.btnBorder} !important`,
                border: "1px solid",
              },
              "&:active": {
                backgroundColor: colors.background.button,
                borderColor: `${colors.gray.btnBorder} !important`,
                border: "1px solid",
              },
              "&:focus": {
                backgroundColor: colors.background.button,
                borderColor: `${colors.gray.btnBorder} !important`,
              },
              "&:disabled": {
                borderColor: colors.gray.disabled,
                backgroundColor: colors.background.default,
                color: colors.gray.disabled,
              },
            },
            "&.success": {
              backgroundColor: colors.green.main,
              borderColor: colors.green.main,
              color: colors.background.default,
              "&:hover": {
                backgroundColor: colors.green.hover,
                borderColor: colors.green.hover,
              },
              "&:focus": {
                borderColor: colors.green.main,
                backgroundColor: colors.green.main,
              },
              "&:active": {
                color: colors.background.default,
                borderColor: colors.green.main,
                backgroundColor: colors.green.main,
              },
              "&:disabled": {
                borderColor: colors.gray.disabled,
                backgroundColor: colors.gray.disabled,
                color: colors.background.default,
              },
            },
          },
        },
      },
      MuiContainer: {
        defaultProps: {
          disableGutters: true,
          maxWidth: false,
          sx: {
            px: {
              xs: "10px",
              md: "50px",
              lg: "250px",
            },
            pl: {
              md: "80px",
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "&.MuiFormControl-root.MuiTextField-root:hover .MuiOutlinedInput-notchedOutline":
              {
                borderColor: colors.gray.disabled,
              },
            "& .MuiInputBase-root": {
              width: "100%",
              height: "40px",
              padding: "10px 16px",
              borderRadius: "4px",
              color: colors.text.primary,
              lineHeight: "normal",
              "& .MuiInputBase-input": {
                padding: 0,
              },
              "&.Mui-disabled": {
                borderColor: colors.gray.disabled,
                cursor: "not-allowed",
                backgroundColor: colors.background.modalBg,
                "& .MuiInputBase-input": {
                  cursor: "not-allowed",
                },
              },
            },
            "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
              border: "1px solid",
              borderColor: colors.gray.disabled,
            },
            "&:hover .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline":
              {
                border: "2px solid",
                borderColor: colors.error.main,
              },
            "& .MuiInputBase-root.Mui-error fieldset": {
              border: "2px solid",
              borderColor: colors.error.main,
            },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                border: "2px solid",
                borderColor: colors.text.secondary,
              },
            "& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline":
              {
                border: "2px solid",
                borderColor: colors.error.main,
              },
            "& .MuiInput-input": {
              "&::placeholder": {
                color: colors.text.secondary,
              },
              "&.Mui-disabled": {
                cursor: "not-allowed",
              },
            },
            "& .MuiInput-input.Mui-disabled ": {
              color: colors.gray.disabled,
            },
            "& .MuiFormHelperText-root": {
              marginTop: "4px",
              padding: "0",
              marginLeft: "0",
              fontFamily: fonts.text,
              fontWeight: 400,
              fontSize: fontSize.xs,
              letterSpacing: 0,
              color: colors.error.main,
            },
            "& .MuiInputAdornment-root": {
              marginLeft: "10px",
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            "&:hover": {
              backgroundColor: "transparent",
            },
            "& .MuiSvgIcon-root": {
              fill: colors.text.primary,
              width: "24px",
              height: "24px",
            },
            "&.Mui-disabled .MuiSvgIcon-root": {
              fill: colors.gray.disabled,
            },
            "&.Mui-checked > .PrivateSwitchBase-input:focus ~ .MuiSvgIcon-root > path":
              {
                outline: "2px solid",
                outlineColor: colors.text.primary,
                borderRadius: "1px",
              },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            backgroundColor: colors.background.default,
            "&:hover": {
              backgroundColor: colors.background.secondary,
            },
            "&.Mui-selected": {
              backgroundColor: colors.background.default,
              "&:hover": {
                backgroundColor: colors.background.secondary,
              },
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            "&.MuiPaper-root": {
              "& .MuiList-root": {
                overflow: "auto",
                maxHeight: "200px",
              },
              "&.MuiDrawer-paper .MuiList-root": {
                overflow: "unset",
                maxHeight: "none",
              },
            },
          },
        },
      },
      MuiPopper: {
        styleOverrides: {
          root: {
            "&.MuiPopper-root": {
              "&.MuiAutocomplete-popper": {
                zIndex: 2000,
              },
            },
          },
        },
      },
      MuiModal: {
        styleOverrides: {
          root: {
            "&.MuiModal-root": {
              zIndex: 1600,
            },
          },
        },
      },
    },
  }) as ThemeOptions;
